import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";

const EMPTY_POST = { slug: "", title: "", excerpt: "", body: "", cover: "", author: "Mark", tags: [] };

export default function BlogEditor() {
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/blog").then((r) => setPosts(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            const payload = { ...data, tags: typeof data.tags === "string" ? data.tags.split(",").map((s) => s.trim()).filter(Boolean) : (data.tags || []) };
            if (data.original_slug) {
                await api.put(`/admin/blog/${data.original_slug}`, payload);
                toast.success("Post updated.");
            } else {
                await api.post("/admin/blog", payload);
                toast.success("Post published.");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (slug) => {
        if (!window.confirm("Delete this post?")) return;
        try {
            await api.delete(`/admin/blog/${slug}`);
            toast.success("Deleted.");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const startEdit = async (p) => {
        const { data } = await api.get(`/blog/${p.slug}`);
        setEditing({ ...data, original_slug: data.slug, tags: (data.tags || []).join(", ") });
    };

    if (editing !== null) {
        return <PostForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />;
    }

    return (
        <div data-testid="blog-editor">
            <div className="flex justify-end mb-6">
                <button onClick={() => setEditing({ ...EMPTY_POST, tags: "" })} className="btn-primary" data-testid="admin-add-post">
                    <Plus size={16} /> New post
                </button>
            </div>
            <div className="space-y-3">
                {posts.map((p) => (
                    <div key={p.id} className="card p-5 flex justify-between gap-4" data-testid={`admin-post-${p.slug}`}>
                        <div className="min-w-0">
                            <div className="overline">{new Date(p.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                            <div className="font-serif text-xl truncate">{p.title}</div>
                            <p className="text-sm text-[var(--sb-text-dim)] mt-1 line-clamp-2">{p.excerpt}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button onClick={() => startEdit(p)} className="text-[var(--sb-text-dim)] hover:text-[var(--sb-brand)] p-2" data-testid={`admin-post-edit-${p.slug}`}><Edit3 size={16} /></button>
                            <button onClick={() => remove(p.slug)} className="text-[var(--sb-text-dim)] hover:text-red-400 p-2" data-testid={`admin-post-delete-${p.slug}`}><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PostForm({ initial, onSave, onCancel }) {
    const [form, setForm] = useState(initial);

    return (
        <div className="card p-8 max-w-4xl" data-testid="post-form">
            <h2 className="font-serif text-2xl mb-6">{form.original_slug ? "Edit post" : "New post"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Title" full><input className="input-line" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="post-form-title" /></Field>
                <Field label="Slug (URL)"><input className="input-line" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} placeholder="my-post-slug" data-testid="post-form-slug" /></Field>
                <Field label="Author"><input className="input-line" value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} /></Field>
                <Field label="Cover image URL" full><input className="input-line" value={form.cover} onChange={(e) => setForm({...form, cover: e.target.value})} /></Field>
                <Field label="Excerpt (short summary)" full><textarea className="input-line" rows={2} value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} /></Field>
                <Field label="Body (use blank lines to separate paragraphs)" full><textarea className="input-line" rows={12} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="post-form-body" /></Field>
                <Field label="Tags (comma separated)" full><input className="input-line" value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="Field notes, Skye" /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-8">
                <button onClick={onCancel} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}}><X size={14} /> Cancel</button>
                <button onClick={() => onSave(form)} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="post-form-save"><Check size={14} /> Save post</button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Pilot's Log editor — progressive-unlock field journal                      */
